import { observable, action, makeObservable } from 'mobx'
import { toStream } from 'utils/mobx-util'
import { filter, skip, take } from 'rxjs/operators'
import { shareLatest, whileActive } from 'utils/rx-util'
import { CancellationToken } from './cancellation'
import { Observable } from 'rxjs'

/**
 * Modal view model base.
 */
export default class ModalViewModel {
  /**
   * Whether the modal is showing or not.
   */
  @observable showing = false

  /**
   * A stream for the showing observable. Emits current value on subscribe.
   */
  showing$ = toStream(() => this.showing, true).pipe(shareLatest())

  /**
   * Emits a value when the modal closes.
   */
  close$ = this.showing$.pipe(
    skip(1),
    filter((v) => !v),
  )

  /**
   * Emits a value when the modal is shown.
   */
  show$ = this.showing$.pipe(
    skip(1),
    filter((v) => v),
  )

  /**
   * Constructor
   *
   * @param cancellationTokenOrActiveObservable When specified, will
   *  close the modal if the cancellation token cancels, or if the active
   *  observable emits `false`.
   */
  constructor(
    cancellationTokenOrActiveObservable: ModalViewModelClosingSignal = CancellationToken.default,
  ) {
    makeObservable(this)
    this.show = action('show', this.show.bind(this))
    this.close = action('close', this.close.bind(this))

    // This little trick registers for cancellation
    // when the modal is shown, and unregisters on close.
    const closingSignal$ = toClosingSignal(cancellationTokenOrActiveObservable)
    closingSignal$.pipe(this.whileShowing()).subscribe(this.close)
  }

  /**
   * Returns an Observable that emits the VM as the only
   * value. Does not complete the Observable. Closes the modal on unsubscribe.
   */
  asObservable() {
    return new Observable<this>((subscriber) => {
      subscriber.next(this)
      return () => this.close()
    })
  }

  /**
   * Show the modal.
   */
  @action
  show() {
    this.showing = true
  }

  /**
   * Close the modal.
   */
  @action
  close() {
    this.showing = false
  }

  /**
   * Operator that subscribes to the source when showing,
   * and unsubscribes when closing.
   *
   * @protected
   */
  protected whileShowing<T>() {
    return whileActive<T>(this.showing$)
  }
}

/**
 * These types can be used to automatically close a Modal:
 *  1. When the Cancellation Token cancels
 *  2. When the `Observable` emits `false`.
 */
export type ModalViewModelClosingSignal =
  | CancellationToken
  | Observable<boolean>

/**
 * Creates an observable that emits a value when the modal should close.
 * If a cancellation token, closes on cancel.
 * If an observable, closes when the observable emits `false`.
 *
 * @param cancellationTokenOrActiveObservable
 */
function toClosingSignal(
  cancellationTokenOrActiveObservable: CancellationToken | Observable<boolean>,
) {
  if ('isCancelled' in cancellationTokenOrActiveObservable) {
    const ct = cancellationTokenOrActiveObservable as CancellationToken
    // It's a cancellation token.
    return new Observable((observer) =>
      ct.onCancel(observer.next.bind(observer)),
    ).pipe(take(1))
  }

  return cancellationTokenOrActiveObservable.pipe(
    filter((v) => !v),
    take(1),
  )
}
